// `@sl/core/viewState` — the view state a URL carries (REQ-021 AC4, REQ-041 AC2, REQ-042 AC4; ARCH §8.1, §9.2).
//
// One `ViewState` describes what the user is looking at across Home, lists, detail pages and Spend & Budget:
// the period (preset, custom dates, comparison) and the list controls (level, parent scope, quick filters,
// sort, search, column preset). `parseViewState` reads it from a URL and NEVER throws: an invalid value falls
// back to its default and is reported in `warnings[]`. `serializeViewState` writes the canonical form — keys
// sorted, defaults omitted, filters in chip order — so two identical views give byte-identical query strings
// and a link reproduces the view (REQ-021 AC4).
//
// Defaults are the product's fixed defaults (REQ-021 AC1: Last 7 days; AC3: compare on; REQ-042 AC3: Spent,
// descending), never the user's preferences: a URL that omitted whatever equalled *my* preference would show a
// colleague a different view. Preferences fill ABSENT fields at parse time (`fallback`, built with
// `viewStateFromPreferences`); every change the user makes is written to the URL and mirrored to preferences
// (ARCH §9.2), so the two never disagree on a field the URL carries.
//
// The web reads / writes the search part of the hash (`useSearchParams`); mobile passes its route params (a
// plain record) — both shapes are accepted (see `searchParams.ts`). Unknown parameters (`demoNow`, `demoFault`,
// …) are ignored here and preserved by `withViewState`.

import type { EntityLevel } from '../types/entities';
import type { IsoDate } from '../types/primitives';
import type { PeriodPreset, UserPreferences } from '../types/settings';
import type { PeriodQuery } from '../api/ApiClient';
import { PeriodError, resolvePeriod, type ResolvedPreset } from '../periods/periods';
import { readParams, writeParams, type ParamSource } from './searchParams';

export type { ParamSource } from './searchParams';

// ---- vocabularies ---------------------------------------------------------------------------------------------

/** The date control's order (REQ-021 AC1). `neighbouringPresets` (queries) and the parser read it. */
export const PERIOD_PRESET_ORDER: readonly PeriodPreset[] = [
  'today',
  'yesterday',
  'last_7d',
  'last_30d',
  'this_month',
  'last_month',
  'custom',
];

/** The quick filter chips, in the order they are shown and serialised (REQ-042 AC2). */
export const LIST_FILTER_KEYS = [
  'needs_attention',
  'running',
  'off',
  'has_problem',
  'learning',
  'spending_no_results',
  'possibly_wasted',
] as const;
export type ListFilterKey = (typeof LIST_FILTER_KEYS)[number];

/** The four column presets of REQ-043 AC1 (same spelling as `UserPreferences.lists[].columnPreset`). */
export const COLUMN_PRESETS = ['money', 'results', 'attention', 'delivery'] as const;
export type ColumnPreset = (typeof COLUMN_PRESETS)[number];

export const ENTITY_LEVELS: readonly EntityLevel[] = ['campaign', 'adset', 'ad'];

export type SortDir = 'asc' | 'desc';
export interface ListSort {
  /** A column key — a metric id (`spend`, `costPerResult`, …) or an entity field; the list owns the set. */
  key: string;
  dir: SortDir;
}

export interface ViewState {
  preset: PeriodPreset;
  /** Custom range only (`preset === 'custom'`); `null` otherwise. */
  since: IsoDate | null;
  until: IsoDate | null;
  /** "Compare to previous period" (REQ-021 AC3) — `compare: 'none'` on the wire when false. */
  compare: boolean;
  /** The list level shown (REQ-041 AC5); campaigns is the tree on web. */
  level: EntityLevel;
  /** Parent scope of an ad-set / ad list (API §4.2 `parentId`); never with `level: 'campaign'`. */
  parentId: string | null;
  /** Active quick filters, AND-ed (REQ-042 AC2), in chip order, no duplicates. */
  filters: ListFilterKey[];
  sort: ListSort;
  /** Search text, trimmed; `''` when none. */
  search: string;
  columnPreset: ColumnPreset;
}

export const DEFAULT_VIEW_STATE: Readonly<ViewState> = Object.freeze<ViewState>({
  preset: 'last_7d',
  since: null,
  until: null,
  compare: true,
  level: 'campaign',
  parentId: null,
  filters: [],
  sort: { key: 'spend', dir: 'desc' },
  search: '',
  columnPreset: 'money',
});

/** The URL parameters this module owns (everything else in a URL is someone else's and left alone). */
export const VIEW_STATE_PARAMS = [
  'period',
  'since',
  'until',
  'compare',
  'level',
  'parentId',
  'filter',
  'sort',
  'dir',
  'q',
  'columns',
] as const;
export type ViewStateParam = (typeof VIEW_STATE_PARAMS)[number];

export const MAX_SEARCH_LENGTH = 200;
export const MAX_ID_LENGTH = 128;

// ---- parsing ----------------------------------------------------------------------------------------------------

export type ViewStateWarningCode =
  | 'unknown_value' // not in the vocabulary (`period=fortnight`, `compare=yes`, `filter=purple`)
  | 'invalid_range' // a custom range `resolvePeriod` rejects: reversed, malformed, after today
  | 'incomplete_range' // `since` without `until` (or the reverse), or `period=custom` with neither
  | 'ignored' // present but meaningless here: dates on a named preset, `parentId` on the campaign level
  | 'duplicate' // the parameter appeared more than once — the first value was used
  | 'malformed' // the value could not be percent-decoded
  | 'too_long'; // truncated (search) or dropped (ids) for length

export interface ViewStateWarning {
  param: ViewStateParam;
  code: ViewStateWarningCode;
  /** The offending value (or values, comma-joined) — for logs, never shown to users as-is. */
  value?: string;
}

export interface ParseViewStateOptions {
  /** The account's `today` (`AccountInfo.today`) — lets a custom range be rejected when it ends in the future.
   *  Without it only the format and order are checked; a future range is then caught by `resolvePeriod` at
   *  fetch time and by the server's 422 (`validation.period.*`). */
  today?: IsoDate;
  /** Values for ABSENT parameters — normally `viewStateFromPreferences(prefs, level)`. Never affects
   *  serialisation. */
  fallback?: Partial<ViewState>;
}

export interface ParsedViewState {
  state: ViewState;
  warnings: ViewStateWarning[];
}

const isPreset = (v: string): v is PeriodPreset => (PERIOD_PRESET_ORDER as string[]).includes(v);
const isLevel = (v: string): v is EntityLevel => (ENTITY_LEVELS as string[]).includes(v);
const isFilter = (v: string): v is ListFilterKey =>
  (LIST_FILTER_KEYS as readonly string[]).includes(v);
const isColumnPreset = (v: string): v is ColumnPreset =>
  (COLUMN_PRESETS as readonly string[]).includes(v);
const isSortDir = (v: string): v is SortDir => v === 'asc' || v === 'desc';

const SORT_KEY = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
// Printable, no whitespace, no control characters — Meta ids are digits, demo ids are short slugs.
const ID_SHAPE = /^[!-~]+$/;

const COMPARE_ON = new Set(['on', 'previous', 'true', '1']);
const COMPARE_OFF = new Set(['off', 'none', 'false', '0']);

/** Read a `ViewState` from a URL. Never throws on input: see the header comment. */
export function parseViewState(
  source: ParamSource,
  options: ParseViewStateOptions = {},
): ParsedViewState {
  const fb: ViewState = { ...DEFAULT_VIEW_STATE, ...stripUndefined(options.fallback) };
  const warnings: ViewStateWarning[] = [];
  const read = readParams(source, VIEW_STATE_PARAMS);

  const one = (param: ViewStateParam): string | null => {
    if (read.malformed.has(param)) warnings.push({ param, code: 'malformed' });
    const values = read.values.get(param);
    if (!values || values.length === 0) return null;
    if (values.length > 1) warnings.push({ param, code: 'duplicate', value: values.join(',') });
    return values[0] ?? null;
  };

  // -- period --------------------------------------------------------------------------------------------------
  const rawPreset = one('period');
  const rawSince = one('since');
  const rawUntil = one('until');
  let preset: PeriodPreset;
  let since: IsoDate | null;
  let until: IsoDate | null;
  if (rawPreset === null && rawSince === null && rawUntil === null) {
    preset = fb.preset;
    since = fb.since;
    until = fb.until;
  } else {
    if (rawPreset === null)
      preset = 'custom'; // dates alone mean a custom range
    else if (isPreset(rawPreset)) preset = rawPreset;
    else {
      warnings.push({ param: 'period', code: 'unknown_value', value: rawPreset });
      preset = namedFallback(fb);
    }
    since = rawSince === '' ? null : rawSince;
    until = rawUntil === '' ? null : rawUntil;
    if (preset !== 'custom' && (since !== null || until !== null)) {
      warnings.push({ param: since !== null ? 'since' : 'until', code: 'ignored' });
      since = null;
      until = null;
    }
  }
  if (preset === 'custom') {
    if (since === null || until === null) {
      warnings.push({ param: since === null ? 'since' : 'until', code: 'incomplete_range' });
      preset = namedFallback(fb);
      since = null;
      until = null;
    } else {
      try {
        // Without `today`, `until` stands in: the format and order are still checked, the future is not.
        resolvePeriod('custom', options.today ?? until, { since, until });
      } catch (e) {
        if (!(e instanceof PeriodError)) throw e;
        warnings.push({ param: 'since', code: 'invalid_range', value: `${since}..${until}` });
        preset = namedFallback(fb);
        since = null;
        until = null;
      }
    }
  }
  if (preset !== 'custom') {
    since = null;
    until = null;
  }

  // -- compare -------------------------------------------------------------------------------------------------
  let compare = fb.compare;
  const rawCompare = one('compare');
  if (rawCompare !== null) {
    const v = rawCompare.toLowerCase();
    if (COMPARE_ON.has(v)) compare = true;
    else if (COMPARE_OFF.has(v)) compare = false;
    else warnings.push({ param: 'compare', code: 'unknown_value', value: rawCompare });
  }

  // -- level + parent ------------------------------------------------------------------------------------------
  let level = fb.level;
  const rawLevel = one('level');
  if (rawLevel !== null) {
    if (isLevel(rawLevel)) level = rawLevel;
    else warnings.push({ param: 'level', code: 'unknown_value', value: rawLevel });
  }
  let parentId = fb.parentId;
  const rawParent = one('parentId');
  if (rawParent !== null) {
    const v = rawParent.trim();
    if (v === '') parentId = null;
    else if (v.length > MAX_ID_LENGTH) {
      warnings.push({ param: 'parentId', code: 'too_long' });
      parentId = null;
    } else if (!ID_SHAPE.test(v)) {
      warnings.push({ param: 'parentId', code: 'malformed', value: v });
      parentId = null;
    } else parentId = v;
  }
  if (level === 'campaign' && parentId !== null) {
    if (rawParent !== null) warnings.push({ param: 'parentId', code: 'ignored', value: parentId });
    parentId = null;
  }

  // -- filters -------------------------------------------------------------------------------------------------
  let filters = canonicalFilters(fb.filters);
  const rawFilter = one('filter');
  if (rawFilter !== null) {
    const tokens = rawFilter
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t !== '');
    const unknown = tokens.filter((t) => !isFilter(t));
    if (unknown.length > 0)
      warnings.push({ param: 'filter', code: 'unknown_value', value: unknown.join(',') });
    filters = canonicalFilters(tokens.filter(isFilter));
  }

  // -- sort ----------------------------------------------------------------------------------------------------
  let sortKey = fb.sort.key;
  let sortDir = fb.sort.dir;
  const rawSort = one('sort');
  if (rawSort !== null) {
    if (SORT_KEY.test(rawSort)) sortKey = rawSort;
    else warnings.push({ param: 'sort', code: 'unknown_value', value: rawSort });
  }
  const rawDir = one('dir');
  if (rawDir !== null) {
    const v = rawDir.toLowerCase();
    if (isSortDir(v)) sortDir = v;
    else warnings.push({ param: 'dir', code: 'unknown_value', value: rawDir });
  }

  // -- search --------------------------------------------------------------------------------------------------
  let search = fb.search.trim();
  const rawSearch = one('q');
  if (rawSearch !== null) {
    search = rawSearch.trim();
    if (search.length > MAX_SEARCH_LENGTH) {
      warnings.push({ param: 'q', code: 'too_long' });
      search = search.slice(0, MAX_SEARCH_LENGTH).trim();
    }
  }

  // -- columns -------------------------------------------------------------------------------------------------
  let columnPreset = fb.columnPreset;
  const rawColumns = one('columns');
  if (rawColumns !== null) {
    if (isColumnPreset(rawColumns)) columnPreset = rawColumns;
    else warnings.push({ param: 'columns', code: 'unknown_value', value: rawColumns });
  }

  return {
    state: {
      preset,
      since,
      until,
      compare,
      level,
      parentId,
      filters,
      sort: { key: sortKey, dir: sortDir },
      search,
      columnPreset,
    },
    warnings,
  };
}

/** The fallback's preset when a custom range cannot be used: its named preset, or the product default. */
function namedFallback(fb: ViewState): PeriodPreset {
  return fb.preset === 'custom' ? DEFAULT_VIEW_STATE.preset : fb.preset;
}

function stripUndefined<T extends object>(o: T | undefined): Partial<T> {
  const out: Partial<T> = {};
  if (!o) return out;
  for (const [k, v] of Object.entries(o))
    if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  return out;
}

/** Chip order, no duplicates. */
export function canonicalFilters(filters: readonly string[]): ListFilterKey[] {
  const set = new Set(filters);
  return LIST_FILTER_KEYS.filter((k) => set.has(k));
}

// ---- serialising -----------------------------------------------------------------------------------------------

/** The canonical pairs of `state` — only what differs from `DEFAULT_VIEW_STATE`, normalised. */
export function viewStatePairs(state: ViewState): [ViewStateParam, string][] {
  const pairs: [ViewStateParam, string][] = [];
  if (state.preset !== DEFAULT_VIEW_STATE.preset) pairs.push(['period', state.preset]);
  if (state.preset === 'custom') {
    if (state.since !== null) pairs.push(['since', state.since]);
    if (state.until !== null) pairs.push(['until', state.until]);
  }
  if (!state.compare) pairs.push(['compare', 'off']);
  if (state.level !== DEFAULT_VIEW_STATE.level) pairs.push(['level', state.level]);
  if (state.parentId !== null && state.level !== 'campaign')
    pairs.push(['parentId', state.parentId]);
  const filters = canonicalFilters(state.filters);
  if (filters.length > 0) pairs.push(['filter', filters.join(',')]);
  if (state.sort.key !== DEFAULT_VIEW_STATE.sort.key) pairs.push(['sort', state.sort.key]);
  if (state.sort.dir !== DEFAULT_VIEW_STATE.sort.dir) pairs.push(['dir', state.sort.dir]);
  const search = state.search.trim();
  if (search !== '') pairs.push(['q', search]);
  if (state.columnPreset !== DEFAULT_VIEW_STATE.columnPreset)
    pairs.push(['columns', state.columnPreset]);
  return pairs;
}

/** Canonical query string for `state` (no leading `?`; `''` for the default view). */
export function serializeViewState(state: ViewState): string {
  return writeParams(viewStatePairs(state));
}

/** `serializeViewState(state)` merged over the parameters already in `source` that are not view state (the
 *  QA switches `demoNow` / `demoScenario` / `demoFault`, …), so the app never drops them when it navigates.
 *  Foreign parameters are found in a string, a record or anything iterable (`URLSearchParams`); an object
 *  that only offers `get` cannot be enumerated, so only the view state is written for it. */
export function withViewState(source: ParamSource, state: ViewState): string {
  const own = new Set<string>(VIEW_STATE_PARAMS);
  const pairs: (readonly [string, string])[] = [];
  for (const [name, values] of readParams(source, VIEW_STATE_PARAMS).values) {
    if (own.has(name)) continue;
    for (const v of values) pairs.push([name, v]);
  }
  pairs.push(...viewStatePairs(state));
  return writeParams(pairs);
}

// ---- bridges ---------------------------------------------------------------------------------------------------

/** The `fallback` for `parseViewState` from the user's saved preferences (API §10) for one list level. */
export function viewStateFromPreferences(
  prefs: UserPreferences,
  level: EntityLevel = DEFAULT_VIEW_STATE.level,
): Partial<ViewState> {
  const list = prefs.lists[level];
  const out: Partial<ViewState> = {
    preset: prefs.period.preset,
    since: prefs.period.preset === 'custom' ? prefs.period.since : null,
    until: prefs.period.preset === 'custom' ? prefs.period.until : null,
    compare: prefs.period.compare,
    level,
  };
  if (list) {
    out.filters = canonicalFilters(list.filters);
    if (SORT_KEY.test(list.sort.key) && isSortDir(list.sort.dir)) out.sort = { ...list.sort };
    if (isColumnPreset(list.columnPreset)) out.columnPreset = list.columnPreset;
  }
  return out;
}

/** The dates of the view's period against the account's `today` (`resolvePeriod` with the custom range). */
export function resolveViewPeriod(
  view: Pick<ViewState, 'preset' | 'since' | 'until'>,
  today: IsoDate,
): ResolvedPreset {
  return resolvePeriod(view.preset, today, {
    since: view.since ?? undefined,
    until: view.until ?? undefined,
  });
}

/** What the period-keyed queries take: `{ since, until, compare }` for the view (`queries.summary(id, …)`). */
export function periodQueryOf(
  view: Pick<ViewState, 'preset' | 'since' | 'until' | 'compare'>,
  today: IsoDate,
): PeriodQuery {
  const r = resolveViewPeriod(view, today);
  return { since: r.since, until: r.until, compare: view.compare ? 'previous' : 'none' };
}
